import type {
  ApiContext,
  ApiNotAuthorized,
  InvalidFormField,
  ShopCapability,
  StoreCapabilities,
  StoreNotFound,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import { type AsyncResult, describeErrors } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { CapabilitiesRepository } from "../repositories/capabilities-repository";
import type { BackendApi } from "./api";

/** Fetches what the shop has switched on into the repository. Single flight per shop. */
export async function loadCapabilities(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  capabilitiesRepository: CapabilitiesRepository;
  storeId: string;
}): Promise<void> {
  const repo = o.capabilitiesRepository;
  if (repo.storeId === o.storeId && repo.status.kind === "loading") return;
  repo.setLoading(o.storeId);
  const r = await o.authHandler.callApi((ctx) => o.client.stores.capabilities(ctx, o.storeId));
  if (repo.storeId !== o.storeId) return;
  if (!r.ok) {
    repo.setError(o.storeId, describeErrors(r.errors));
    return;
  }
  repo.setLoaded(o.storeId, r.value);
}

/** Asks HobbyScan to switch `capability` on, answered at `contactEmail` (else the shop's own); the answer replaces what the shell shows. */
export async function requestCapability(o: {
  client: BackendApi;
  authHandler: AuthHandler<ApiContext>;
  capabilitiesRepository: CapabilitiesRepository;
  storeId: string;
  capability: ShopCapability;
  contactEmail?: string;
}): AsyncResult<StoreCapabilities, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | InvalidFormField> {
  const r = await o.authHandler.callApi((ctx) =>
    o.client.stores.requestCapability(ctx, o.storeId, o.capability, o.contactEmail),
  );
  if (r.ok && o.capabilitiesRepository.storeId === o.storeId) o.capabilitiesRepository.setLoaded(o.storeId, r.value);
  return r;
}
